/**
 * Builds the sticky UGC widget's slides from a server-rendered pool of candidate
 * images (one per eligible product photo), picking a random subset on every page
 * load so the mix varies from visit to visit — then cycles them and handles dismissal.
 *
 * Closing hides the widget for the rest of the browser session (sessionStorage), so it
 * doesn't keep reappearing on every product page once a shopper has dismissed it. An
 * inline script in sticky-ugc-slideshow.liquid applies the same check before paint to
 * avoid a flash; this repeats it so no slides or images load for a dismissed widget.
 * The theme editor ignores the stored dismissal so the section stays editable.
 */
const DISMISSED_STORAGE_KEY = 'sticky-ugc-slideshow-dismissed';

class StickyUgcSlideshowComponent extends HTMLElement {
  connectedCallback() {
    if (this.#isDismissedThisSession()) {
      this.hidden = true;
      return;
    }

    this.track = this.querySelector('.sticky-ugc-slideshow__track');
    this.#renderRandomSlides();

    this.slides = Array.from(this.querySelectorAll('.sticky-ugc-slideshow__slide'));
    if (this.slides.length === 0) return;
    this.activeIndex = 0;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.slides.length > 1 && !reducedMotion) {
      const interval = Number(this.dataset.interval) || 4000;
      this.timer = setInterval(() => this.showNextSlide(), interval);
    }

    const closeButton = this.querySelector('.sticky-ugc-slideshow__close');
    closeButton?.addEventListener('click', () => this.dismiss());

    this.#watchStickyAddToCartBar();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.stickyBarObserver?.disconnect();
  }

  /**
   * Reads the JSON pool of {html, url, title} candidates (one per eligible product
   * photo, rendered server-side), shuffles it, and builds a slide — an image linked
   * to its product's page — for a random subset of it.
   */
  #renderRandomSlides() {
    const source = this.querySelector('script[data-sticky-ugc-slideshow-source]');
    if (!source) return;

    let entries;
    try {
      entries = JSON.parse(source.textContent);
    } catch {
      return;
    }
    if (!Array.isArray(entries) || entries.length === 0) return;

    const slideCount = Math.min(Number(this.dataset.slideCount) || entries.length, entries.length);
    const picked = this.#shuffle(entries).slice(0, slideCount);

    picked.forEach((entry, index) => {
      const slide = document.createElement('div');
      slide.className = index === 0 ? 'sticky-ugc-slideshow__slide is-active' : 'sticky-ugc-slideshow__slide';

      const link = document.createElement('a');
      link.href = entry.url;
      link.className = 'sticky-ugc-slideshow__link';
      link.setAttribute('aria-label', entry.title);
      link.innerHTML = entry.html;

      slide.appendChild(link);
      this.track.appendChild(slide);
    });

    // The active slide is visible immediately, so load it eagerly rather than lazily.
    const activeImage = this.track.querySelector('.sticky-ugc-slideshow__slide.is-active img');
    if (activeImage) {
      activeImage.loading = 'eager';
      activeImage.fetchPriority = 'high';
    }
  }

  #shuffle(array) {
    const result = array.slice();
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  /**
   * The sticky add-to-cart bar toggles `data-stuck="true"` on itself once the shopper
   * scrolls past the buy buttons (see sticky-add-to-cart.js). While it's stuck to the
   * bottom of the screen, raise this widget above it so the two don't overlap.
   */
  #watchStickyAddToCartBar() {
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar) return;

    const syncRaisedState = () => {
      const isStuck = bar.getAttribute('data-stuck') === 'true';
      if (isStuck) {
        this.style.setProperty('--sticky-ugc-slideshow-raise', `${bar.offsetHeight + 12}px`);
      }
      this.dataset.raised = String(isStuck);
    };

    syncRaisedState();
    this.stickyBarObserver = new MutationObserver(syncRaisedState);
    this.stickyBarObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  }

  showNextSlide() {
    this.slides[this.activeIndex].classList.remove('is-active');
    this.activeIndex = (this.activeIndex + 1) % this.slides.length;
    this.slides[this.activeIndex].classList.add('is-active');
  }

  dismiss() {
    clearInterval(this.timer);
    this.hidden = true;

    try {
      sessionStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
    } catch {
      // Storage can be unavailable (e.g. blocked site data) — fall back to per-page dismissal.
    }

    trackEvent('ugc_slideshow_close', { page_path: window.location.pathname });
  }

  #isDismissedThisSession() {
    if (window.Shopify?.designMode) return false;

    try {
      return sessionStorage.getItem(DISMISSED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }
}

/**
 * Sends an event to Google Analytics through whichever channel is on the page —
 * `gtag()`, a GTM `dataLayer`, and `Shopify.analytics.publish()` for a Customer Events
 * custom pixel. Same approach as the WhatsApp click tracking in whatsapp-sticky-button.js.
 *
 * @param {string} name
 * @param {Record<string, string>} params
 */
function trackEvent(name, params) {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    } else if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...params });
    }

    window.Shopify?.analytics?.publish?.(name, params);
  } catch (error) {
    console.warn(`${name} tracking failed`, error);
  }
}

if (!customElements.get('sticky-ugc-slideshow-component')) {
  customElements.define('sticky-ugc-slideshow-component', StickyUgcSlideshowComponent);
}
